import React from 'react';
import {
  ArrowDown,
  ArrowRight,
  Brain,
  Code2,
  FileText,
  MessagesSquare,
  Scale,
  Sparkles,
  UserRound,
  Video,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import CaseStudyProof, { caseStudyProofs } from '../../../Blog/CaseStudyProof';
import {
  CapabilityGrid,
  DEMO_PATH,
  EvidenceBoard,
  IndustryCta,
  IndustryFlowStyles,
  IndustryWorkflow,
  ProblemGrid,
  Section,
  SectionIntro,
} from './industryUi';

const PROBLEMS = [
  {
    title: 'Resume-only screening',
    detail: 'A resume lists tools. It does not show whether the candidate can build with them.',
    icon: FileText,
  },
  {
    title: 'Generic interviews',
    detail: 'Unstructured conversations make the same role hard to compare across candidates.',
    icon: MessagesSquare,
  },
  {
    title: 'Manual technical screening',
    detail: 'Engineers spend early rounds on checks a structured assessment can cover first.',
    icon: Code2,
  },
  {
    title: 'Disconnected assessments',
    detail: 'Resume notes, tests, and interviews live in different places, so the picture stays split.',
    icon: Scale,
  },
];

const WORKFLOW = [
  {
    title: 'Resume Intelligence',
    detail: 'Score the resume against the job description before anyone is scheduled.',
    icon: FileText,
  },
  {
    title: 'Coding Assessment',
    detail: 'Run a proctored coding test so the submission itself is part of the evidence.',
    icon: Code2,
  },
  {
    title: 'AI Follow-up',
    detail: 'Ask about the code the candidate submitted, not a generic puzzle.',
    icon: MessagesSquare,
  },
  {
    title: 'Technical Interview',
    detail: 'Use a role-based AI video interview, then a live round when the team is ready.',
    icon: Video,
  },
  {
    title: 'Behavioural Assessment',
    detail: 'Add behaviour and communication signals beside the technical work.',
    icon: Brain,
  },
  {
    title: 'Human Decision',
    detail: 'A recruiter or hiring manager reviews the evidence and decides.',
    icon: UserRound,
  },
];

const ROLES = [
  'Software Engineer',
  'Full Stack Developer',
  'Frontend Engineer',
  'Backend Engineer',
  'Data Engineer',
  'Data Scientist',
  'AI / ML Engineer',
  'DevOps',
  'Cloud Engineer',
  'Product Manager',
  'Engineering Manager',
  'QA / SDET',
  'Forward Deployed Engineer (FDE)',
  'Site Reliability Engineer',
  'Solutions Architect',
  'Technical Program Manager',
];

const CAPABILITIES = [
  {
    title: 'AI Sourcing & Screening',
    helps: 'How closely the resume matches this role.',
    href: '/hirewithus/ai-sourcing-screening',
    icon: FileText,
  },
  {
    title: 'Coding Assessment',
    helps: 'Whether the candidate can write and explain the work.',
    href: '/hirewithus/coding-assessment',
    icon: Code2,
  },
  {
    title: 'Screening Assessment',
    helps: 'Role-based technical screening before a live interview.',
    href: '/hirewithus/screening-assessment',
    icon: Sparkles,
  },
  {
    title: 'Functional Assessment',
    helps: 'Behaviour, personality, and communication alongside the code.',
    href: '/hirewithus/functional-assessment',
    icon: Brain,
  },
  {
    title: 'Virtual Hiring',
    helps: 'A role-based AI video interview the team can review.',
    href: '/hirewithus/virtualhiring',
    icon: Video,
  },
];

const EVIDENCE = [
  'Resume',
  'Coding',
  'AI follow-up',
  'Technical interview',
  'Behaviour',
];

const DECISIONS = [
  { title: 'AI evaluation', detail: 'Screening, tests, and interviews collect the signals.' },
  { title: 'Candidate evidence', detail: 'The recruiter reviews the resume, test, interview, and behaviour records.' },
  { title: 'Recruiter review', detail: 'A person checks the work, not only the score.' },
  { title: 'Hire decision', detail: 'The team decides who moves forward.' },
];

export default function TechnologyHiring() {
  useSEO({
    title: 'AI Recruitment for IT & Technology | SearchJob.ai',
    description:
      'Technical recruitment with AI resume screening, coding assessments, AI interviews and candidate intelligence.',
    canonical: 'https://searchjob.ai/industries/technology',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <Problem />
        </ScrollReveal>
        <ScrollReveal>
          <Workflow />
        </ScrollReveal>
        <ScrollReveal>
          <Evidence />
        </ScrollReveal>
        <ScrollReveal>
          <Roles />
        </ScrollReveal>
        <ScrollReveal>
          <Capabilities />
        </ScrollReveal>
        <ScrollReveal>
          <HumanDecision />
        </ScrollReveal>
        <ScrollReveal>
          <Proof />
        </ScrollReveal>
        <FinalCta />
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-background">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-[-12%] left-[6%] h-[46%] w-[42%] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute right-[4%] top-[8%] h-[36%] w-[32%] rounded-full bg-emerald-500/10 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 pt-12 pb-16 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-8 lg:pt-16">
        <div className="text-center lg:text-left">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            IT & Technology
          </p>
          <h1 className="mb-6 text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3.4rem] xl:text-6xl">
            Hire Engineers for <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">What They Can Actually Build.</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl lg:mx-0">
            AI-powered technical screening, coding assessment and role-specific interviews for technology teams.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-8 text-base font-semibold !text-primary-foreground shadow-lg shadow-primary/25 group hover:!bg-primary/90"
            >
              <a href={DEMO_PATH}>
                Build Your Tech Hiring Pipeline
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!h-12 !rounded-full bg-card/70 px-8 text-base font-semibold backdrop-blur-md"
            >
              <a href="#technical-workflow">See Technical Screening</a>
            </Button>
          </div>
        </div>
        <HeroFlow />
      </div>
    </section>
  );
}

function HeroFlow() {
  const steps = [
    { label: 'Candidate profile', note: 'Role and resume' },
    { label: 'Coding assessment', note: 'The submitted work' },
    { label: 'AI technical interview', note: 'Questions from the role' },
    { label: 'Evidence for review', note: 'Ready for a person' },
  ];
  return (
    <div className="relative self-start" aria-hidden="true">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[3rem] bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl" />
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Example evaluation
      </p>
      <ol className="space-y-3">
        {steps.map((step, index) => (
          <li key={step.label}>
            <div className="flex items-center gap-3 rounded-2xl border border-primary bg-primary/5 px-4 py-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-sm font-semibold text-primary">
                {index + 1}
              </span>
              <span>
                <span className="block text-sm font-semibold text-foreground">{step.label}</span>
                <span className="block text-sm text-muted-foreground">{step.note}</span>
              </span>
            </div>
            {index < steps.length - 1 ? (
              <ArrowDown className="mx-auto my-1 h-4 w-4 text-primary/70" />
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

function Problem() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="The problem"
        title="Don't just test the resume."
        copy="Technical hiring needs evidence of the work, not only a list of skills."
      />
      <ProblemGrid items={PROBLEMS} />
    </Section>
  );
}

function Workflow() {
  return (
    <Section id="technical-workflow">
      <SectionIntro
        eyebrow="Technical hiring workflow"
        title="From resume to a human decision."
        copy="Select a step. Each one adds a different kind of evidence."
      />
      <IndustryWorkflow steps={WORKFLOW} />
    </Section>
  );
}

function Evidence() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Candidate evidence"
        title="Several signals. One review."
        copy="SearchJob.ai keeps the technical signals a recruiter needs to compare candidates. This is an example of that review, not a live candidate."
      />
      <EvidenceBoard
        profileMark="SE"
        profileTitle="Software Engineer"
        profileNote="Sample role"
        signals={EVIDENCE}
        reviewLabel="Evidence for review"
        nextLabel="Next: a person decides"
        variant="technical"
      />
    </Section>
  );
}

function Roles() {
  return (
    <Section>
      <SectionIntro
        eyebrow="Roles"
        title="Technology roles this workflow can support."
        copy="The same evidence path applies across engineering, data, and technical leadership hiring."
      />
      <ul className="flex flex-wrap gap-3">
        {ROLES.map((role) => (
          <li key={role}>
            <span className="inline-flex min-h-11 items-center rounded-full border border-primary/30 bg-primary/5 px-4 text-sm font-medium text-foreground transition-colors duration-200 hover:border-primary">
              {role}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Capabilities() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Product capabilities"
        title="What each product helps a recruiter see."
        copy="These are products already on SearchJob.ai. AI follow-up sits inside Coding Assessment."
      />
      <CapabilityGrid items={CAPABILITIES} />
    </Section>
  );
}

function HumanDecision() {
  return (
    <Section>
      <SectionIntro
        eyebrow="Human decision"
        title="AI gathers evidence. People hire."
        copy="SearchJob.ai does not replace the hiring decision. It gives the team a clearer record before they make it."
      />
      <ol className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {DECISIONS.map((step, index) => (
          <li key={step.title} className="rounded-2xl border border-primary bg-primary/5 p-5">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">0{index + 1}</span>
            <h3 className="mt-2 text-base font-semibold text-foreground">{step.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.detail}</p>
          </li>
        ))}
      </ol>
    </Section>
  );
}

function Proof() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Customer proof"
        title="A technical hiring story already on the site."
        copy="manest.ai is the approved case study for technical screening and AI-assisted evaluation."
      />
      <CaseStudyProof items={[caseStudyProofs.manest]} />
    </Section>
  );
}

function FinalCta() {
  return (
    <IndustryCta
      title="Build Your Tech Hiring Pipeline"
      copy="SearchJob.ai can combine screening, assessment, technical evaluation, and recruiter review for technology roles."
      primaryLabel="Book a Demo"
      primaryHref={DEMO_PATH}
    />
  );
}
